import { useApolloClient } from '@apollo/client';
import type { DeliveryZone } from '@choco-admin/types';
import { useCallback, useState } from 'react';

import {
  GetSupplierDetailsDataDocument,
  type GetSupplierDetailsDataQuery,
  useSuppliersPageUpdateSupplierDeliveryZonesMutation,
} from '../../../generated';
import {
  areDeliveryZonesEqual,
  CONFLICTING_ZONES_ERROR,
  describeDeliveryZoneSaveError,
  toDeliveryZoneInput,
} from '../utils/deliveryZones';

interface UseSupplierDeliveryZonesResult {
  /**
   * @param zones the full set to keep, since the backend replaces the array
   * @param baselineZones what the editor loaded, so a save that would overwrite
   *   somebody else's change can be refused instead of making it
   */
  saveZones: (
    zones: DeliveryZone[],
    baselineZones: DeliveryZone[]
  ) => Promise<boolean>;
  isSaving: boolean;
  saveError: string | undefined;
  clearSaveError: () => void;
}

/**
 * Persist a distributor's delivery areas.
 *
 * Sends `id` plus `deliveryZones` and nothing else, and stays a separate
 * mutation from the supplier form's own save for two reasons that survive
 * `buildSupplierUpdateInput` now sending only edited fields.
 *
 * The first is the conflict check. A zone save reads the distributor's current
 * areas and refuses when they no longer match what the editor loaded, because
 * the array is replaced wholesale and there is no revision check server-side
 * (see CRM-459). That read belongs to this save, not to a form submit that
 * happens to include zones.
 *
 * The second is the narrow selection set. This mutation asks for `id` and
 * `deliveryZones` only, which is what makes the errors-without-payload check
 * below meaningful: there are no other resolvers whose failure could populate
 * `errors` while the write itself succeeded.
 *
 * The zones tab also holds its own `useState` rather than react-hook-form, so
 * the form's dirty-field tracking never sees a polygon move.
 *
 * The backend replaces the whole array, so callers pass the full set they want
 * to keep, including untouched zones.
 */
export const useSupplierDeliveryZones = (
  supplierId: string | undefined
): UseSupplierDeliveryZonesResult => {
  const [updateSupplier, { loading: isSaving }] =
    useSuppliersPageUpdateSupplierDeliveryZonesMutation();
  const [saveError, setSaveError] = useState<string>();
  const client = useApolloClient();

  const saveZones = useCallback(
    async (
      zones: DeliveryZone[],
      baselineZones: DeliveryZone[]
    ): Promise<boolean> => {
      if (!supplierId) return false;

      setSaveError(undefined);

      try {
        // The backend replaces the whole array and has no version check, so a
        // save carries every area the editor happens to be holding. Two people
        // on the same distributor is enough to lose one: whoever saves second
        // writes their own list over the first one's, and the areas the first
        // added are gone for good.
        //
        // Read the current areas before writing. If they no longer match what
        // this editor loaded, somebody changed them, and this save would delete
        // that change. Refuse it and say so. A compare-and-swap in the backend
        // would close the remaining gap between this read and the write.
        const latest = await client.query<GetSupplierDetailsDataQuery>({
          query: GetSupplierDetailsDataDocument,
          variables: { id: supplierId },
          fetchPolicy: 'network-only',
        });
        const serverZones = latest.data?.supplier?.deliveryZones;

        if (serverZones && !areDeliveryZonesEqual(serverZones, baselineZones)) {
          setSaveError(CONFLICTING_ZONES_ERROR);
          return false;
        }

        const result = await updateSupplier({
          variables: {
            supplier: {
              id: supplierId,
              deliveryZones: toDeliveryZoneInput(zones),
            },
          },
          // Mutations run `no-cache` app-wide, so the response never reaches the
          // cache and `supplier.deliveryZones` stays as it was loaded. Without a
          // refetch the editor compares fresh local zones against stale server
          // ones: Save and Discard stay enabled after a successful save, and
          // Discard removes areas that are already persisted.
          refetchQueries: ['GetSupplierDetailsData'],
          awaitRefetchQueries: true,
        });

        // The client sets `mutate.errorPolicy: 'all'` app-wide, so a rejected
        // mutation RESOLVES with an `errors` array instead of throwing. Relying
        // on the catch alone would report a save that never happened.
        //
        // Errors alone are not enough to call it failed, though. This mutation
        // selects only `id` and `deliveryZones` precisely so there are no other
        // resolvers that can fail beside a committed write, but the check still
        // requires the payload to be absent before reporting failure, so a
        // partial response can never send someone to retry a save that landed.
        const [firstError] = result.errors ?? [];
        if (firstError && !result.data?.supplierUpdate) {
          // A missing payload is not proof the write was rejected.
          // `supplierService` writes the row and then awaits an SNS
          // publication, so a publication failure arrives looking exactly like
          // a failed save. Retrying is worse than doing nothing: the array is
          // replaced wholesale, so resending this stale set would erase an area
          // a colleague added in between.
          //
          // The refetch above has already run and awaited, so the cache holds
          // what the backend now has. If it matches what was sent, the write
          // landed and only the announcement failed.
          const refreshed = client.readQuery<GetSupplierDetailsDataQuery>({
            query: GetSupplierDetailsDataDocument,
            variables: { id: supplierId },
          });
          const storedZones = refreshed?.supplier?.deliveryZones;

          if (storedZones && areDeliveryZonesEqual(storedZones, zones)) {
            return true;
          }

          setSaveError(describeDeliveryZoneSaveError(firstError));
          return false;
        }

        return true;
      } catch (error) {
        setSaveError(describeDeliveryZoneSaveError(error));
        return false;
      }
    },
    [supplierId, updateSupplier, client]
  );

  return {
    saveZones,
    isSaving,
    saveError,
    clearSaveError: useCallback(() => setSaveError(undefined), []),
  };
};
